import React, { useState } from "react";

const MahilaMandiComplaintHub = () => {
  const [complaints, setComplaints] = useState([
    {
      id: 1,
      title: "Delayed Delivery of Cotton Yarn",
      type: "Logistics",
      description: "Yarn delivery was 7 days late, disrupting weaving schedule for the women's cooperative.",
      location: "Surat, Gujarat",
      status: "Open",
    },
    {
      id: 2,
      title: "Poor Quality Beads in Jewelry Kit",
      type: "Quality",
      description: "Beads broke easily during crafting, affecting production quality for artisan group.",
      location: "Jaipur, Rajasthan",
      status: "In Progress",
    },
    {
      id: 3,
      title: "Payment Delay from Retail Buyer",
      type: "Finance",
      description: "Payment pending for over 3 weeks for handmade soap order.",
      location: "Mumbai, Maharashtra",
      status: "Resolved",
    },
    {
      id: 4,
      title: "Defective Packaging for Pickle Jars",
      type: "Quality",
      description: "Jars arrived cracked, leading to packaging issues in the women's pickle unit.",
      location: "Chennai, Tamil Nadu",
      status: "Open",
    },
    {
      id: 5,
      title: "Training Session Cancellation for Baking",
      type: "Services",
      description: "Scheduled baking workshop for enterprise was canceled without notice.",
      location: "Delhi",
      status: "In Progress",
    },
    {
      id: 6,
      title: "Substandard Wax for Candle Making",
      type: "Quality",
      description: "Wax did not burn evenly, causing defects in handmade candles.",
      location: "Kolkata, West Bengal",
      status: "Resolved",
    },
    {
      id: 7,
      title: "Loom Rental Equipment Malfunction",
      type: "Rental",
      description: "Rented loom broke mid-use, halting textile production for the group.",
      location: "Varanasi, Uttar Pradesh",
      status: "Open",
    },
    {
      id: 8,
      title: "Delayed Import of Natural Dyes",
      type: "Logistics",
      description: "Dyes shipment from abroad was held up at customs, delaying dyeing workshop.",
      location: "Bangalore, Karnataka",
      status: "In Progress",
    },
    {
      id: 9,
      title: "Incorrect Quantity of Herbal Leaves",
      type: "Quality",
      description: "Received only half the ordered dried tulsi leaves, impacting tea blending.",
      location: "Dehradun, Uttarakhand",
      status: "Resolved",
    },
    {
      id: 10,
      title: "Finance Dispute on Export Order",
      type: "Finance",
      description: "Disagreement on payment terms for embroidered textiles export to USA.",
      location: "Hyderabad, Telangana",
      status: "Open",
    },
  ]);

  const [newComplaint, setNewComplaint] = useState({
    title: "",
    type: "",
    description: "",
    location: "",
    status: "Open",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewComplaint({ ...newComplaint, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newComplaint.title || !newComplaint.type || !newComplaint.description || !newComplaint.location) {
      alert("Please fill in all fields before submitting.");
      return;
    }
    setComplaints([...complaints, { id: complaints.length + 1, ...newComplaint }]);
    setNewComplaint({ title: "", type: "", description: "", location: "", status: "Open" });
  };

  return (
    <section className="py-12 px-6 md:px-20 bg-black text-white min-h-screen">
      {/* Hero Section */}
      <div className="text-center mb-4 py-4 px-4 border border-green-500">
        <h1 className="text-xl lg:text-2xl font-bold text-yellow-200 mb-4">
          👩‍🔧 MahilaMandi Complaint Hub
        </h1>
        <p className="text-white mx-auto">
          Report, track, and resolve issues in women-led enterprises — from raw materials to global trade — in a transparent way.
        </p>
      </div>

      {/* Form to Create New Complaint */}
      <div className="lg:max-w-3xl mx-auto bg-black text-white border border-green-500 shadow-lg rounded-2xl p-6 mb-4">
        <h2 className="text-lg lg:text-xl font-semibold text-yellow-200 mb-4">Register a Complaint</h2>
        <form onSubmit={handleSubmit} className="grid gap-4">
          <input
            type="text"
            name="title"
            value={newComplaint.title}
            onChange={handleChange}
            placeholder="Complaint Title (e.g., Delayed Yarn Delivery)"
            className="border rounded-lg px-3 py-2 w-full text-black"
          />

          <select
            name="type"
            value={newComplaint.type}
            onChange={handleChange}
            className="border rounded-lg px-3 py-2 w-full h-10 text-black"
          >
            <option value="">Select Complaint Type</option>
            <option>Logistics</option>
            <option>Quality</option>
            <option>Finance</option>
            <option>Services</option>
            <option>Rental</option>
            <option>Export-Import</option>
            <option>Others</option>
          </select>

          <textarea
            name="description"
            value={newComplaint.description}
            onChange={handleChange}
            placeholder="Brief issue description"
            style={{ height: "250px", width: "100%" }}
            className="border rounded-lg px-2 py-1 w-full text-sm resize-none text-white"
          />

          <input
            type="text"
            name="location"
            value={newComplaint.location}
            onChange={handleChange}
            placeholder="Location (e.g., Surat, Gujarat)"
            className="border rounded-lg px-3 py-2 w-full text-black"
          />
          <button
            type="submit"
            className="bg-red-700 text-white py-2 w-48 rounded-lg hover:bg-red-800 transition"
          >
            🚩 Submit Complaint
          </button>
        </form>
      </div>

      {/* Complaints Table */}
      <div className="max-w-5xl mx-auto px-3 sm:px-6">
        <h2 className="text-lg sm:text-xl font-semibold text-yellow-200 mb-2 sm:mb-2 text-center">
          🧾 All Complaints
        </h2>

        {/* Scroll container */}
        <div className="overflow-x-auto rounded-xl border border-pink-600 shadow-md">
          <table className="min-w-full bg-gray-900 text-gray-100 text-sm sm:text-base">
            <thead className="bg-pink-700 text-white">
              <tr>
                <th className="p-3 text-left whitespace-nowrap">Title</th>
                <th className="p-3 text-left whitespace-nowrap">Type</th>
                <th className="p-3 text-left whitespace-nowrap">Description</th>
                <th className="p-3 text-left whitespace-nowrap">Location</th>
                <th className="p-3 text-left whitespace-nowrap">Status</th>
              </tr>
            </thead>

            <tbody>
              {complaints.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-gray-700 hover:bg-pink-900/40 transition-colors duration-200"
                >
                  <td className="p-3 text-white whitespace-nowrap">{c.title}</td>
                  <td className="p-3 text-pink-300 whitespace-nowrap">{c.type}</td>
                  <td className="p-3 text-gray-300 min-w-[200px]">{c.description}</td>
                  <td className="p-3 whitespace-nowrap">{c.location}</td>
                  <td className="p-3 text-center whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        c.status === "Open"
                          ? "bg-red-700/40 text-red-300 border border-red-500"
                          : c.status === "In Progress"
                          ? "bg-yellow-700/40 text-yellow-300 border border-yellow-500"
                          : "bg-green-700/40 text-green-300 border border-green-500"
                      }`}
                    >
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default MahilaMandiComplaintHub;